import OpenInNew from "@mui/icons-material/OpenInNew";
import { Menu, Stack } from "@mui/material";
import React, { useState } from "react";
import { CachePolicies } from "use-http";

import { JANZU_URL } from "config/config";
import { BoundingBoxesProvider } from "context/boundingBoxesContext";
import { QueryDetailsModal } from "features/queryDetailsModal/QueryDetailsModal";
import { useFetchCustom } from "hooks/useFetchCustom";
import { TextFieldInput } from "new-components/textFieldInput/TextFieldInput";
import { ListResponse } from "types/api";
import { Detector } from "types/detectors";
import { ImageQuery } from "types/imageQueries";

type ReviewPageSubNavBarQueryDetailsMenuProps = {
  detectorId?: string;
  imageQueryId?: string;
  anchorEl: Element | null;
  onClose: () => void;
};

export function ReviewPageSubNavBarQueryDetailsMenu({
  detectorId = "",
  imageQueryId,
  anchorEl,
  onClose,
}: ReviewPageSubNavBarQueryDetailsMenuProps) {
  const [enteredDetectorId, setEnteredDetectorId] = useState(detectorId);
  const [enteredImageQueryId, setEnteredImageQueryId] = useState(imageQueryId);
  const [hasValidatedDetector, setHasValidatedDetector] = useState(false);
  const [hasValidatedQuery, setHasValidatedQuery] = useState(false);

  const [selectedRowIndex, setSelectedRowIndex] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const {
    get: getDetector,
    error: detectorError,
    data: enteredDetectorData,
    loading: isLoadingDetector,
    response: detectorResponse,
  } = useFetchCustom<Detector>(`${JANZU_URL}reef-api/detectors`, {
    cachePolicy: CachePolicies.NO_CACHE,
    shouldShowErrorSnackbar: (response) => response.status !== 403 && response.status !== 404,
  });

  const {
    get: getImageQuery,
    error: imageQueryError,
    loading: isLoadingImageQuery,
    response: imageQueryResponse,
  } = useFetchCustom<ImageQuery>(`${JANZU_URL}reef-api/image-queries/${enteredImageQueryId}`, {
    cachePolicy: CachePolicies.NO_CACHE,
    shouldShowErrorSnackbar: (response) => response.status !== 403 && response.status !== 404,
  });

  const {
    get: getImageQueriesForDetector,
    data: imageQueriesForDetector,
    loading: isLoadingImageQueriesForDetector,
  } = useFetchCustom<ListResponse<ImageQuery>>(`${JANZU_URL}reef-api/image-queries`, {
    cachePolicy: CachePolicies.NO_CACHE,
  });

  const isDetectorIdInvalid = hasValidatedDetector && (detectorError !== undefined || !enteredDetectorId?.trim());
  const isQueryIdInvalid = hasValidatedQuery && (imageQueryError !== undefined || !enteredImageQueryId?.trim());

  const [imageQueries, setImageQueries] = useState<ImageQuery[] | null>(null);
  const [openModal, setOpenModal] = useState<"image-query" | "detector" | null>(null);

  const fetchNewImageQueries = async (params: {
    enteredDetectorId: string;
    pageSize: number;
    paginateFrom?: string | null;
  }) => {
    const searchParams = new URLSearchParams();
    searchParams.set("detector_id", params.enteredDetectorId);
    searchParams.set("page_size", params.pageSize.toString());

    if (params.paginateFrom) {
      searchParams.set("paginate_from", params.paginateFrom);
    }

    const imageQueries = await getImageQueriesForDetector(`?${searchParams.toString()}`);
    setImageQueries(imageQueries.results);
  };

  const handleOpenForDetector = async () => {
    setImageQueries(null);
    const detector = await getDetector(enteredDetectorId);

    if (detectorResponse.status !== 200) {
      return;
    }

    setOpenModal("detector");

    fetchNewImageQueries({
      enteredDetectorId: detector.id,
      pageSize,
    });
  };

  const handleOpenForImageQuery = async () => {
    setImageQueries(null);
    const imageQuery = await getImageQuery();

    if (imageQueryResponse.status !== 200) {
      return;
    }

    setOpenModal("image-query");
    await getDetector(imageQuery.predictor_id);
    setImageQueries([imageQuery]);
  };

  const isLoading =
    isLoadingImageQuery || isLoadingImageQueriesForDetector || isLoadingDetector || imageQueries === null;

  return (
    <>
      <Menu
        data-testid="review-feed-header-query-details-menu"
        anchorEl={anchorEl}
        open={anchorEl !== null}
        onClose={() => {
          onClose();

          // resets inputs and validation state on close so opening again starts with clean state
          setEnteredDetectorId(detectorId);
          setEnteredImageQueryId(imageQueryId);
          setHasValidatedDetector(false);
          setHasValidatedQuery(false);
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        MenuListProps={{
          sx: {
            padding: 0,
          },
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 0,
            },
          },
        }}
      >
        <Stack width="398px" padding="10px" gap="20px">
          <TextFieldInput
            variant="standard"
            label="Detector id"
            endIcon={OpenInNew}
            error={isDetectorIdInvalid}
            helperText={isDetectorIdInvalid ? "Input not valid" : undefined}
            value={enteredDetectorId}
            onChange={(event) => {
              const { value } = event.target;
              setEnteredDetectorId(value);
              setHasValidatedDetector(false);
            }}
            onKeydown={(event) => {
              if (event.key === "Enter") {
                setHasValidatedDetector(true);
                handleOpenForDetector();
              }
              event.stopPropagation();
            }}
            onEndIconClick={() => {
              setHasValidatedDetector(true);
              handleOpenForDetector();
            }}
          />
          <TextFieldInput
            variant="standard"
            label="Query id"
            endIcon={OpenInNew}
            error={isQueryIdInvalid}
            helperText={isQueryIdInvalid ? "Input not valid" : undefined}
            value={enteredImageQueryId}
            onChange={(event) => {
              const { value } = event.target;
              setEnteredImageQueryId(value);
              setHasValidatedQuery(false);
            }}
            onKeydown={(event) => {
              if (event.key === "Enter") {
                setHasValidatedQuery(true);
                handleOpenForImageQuery();
              }
              event.stopPropagation();
            }}
            onEndIconClick={() => {
              setHasValidatedQuery(true);
              handleOpenForImageQuery();
            }}
          />
        </Stack>
      </Menu>

      {enteredDetectorData && !isLoadingDetector && (
        <BoundingBoxesProvider>
          <QueryDetailsModal
            onClose={() => {
              setOpenModal(null);

              // reset modal state on close
              setSelectedRowIndex(0);
              setPage(1);
            }}
            detector={enteredDetectorData}
            page={openModal === "image-query" ? 1 : page}
            setPage={(newPage) => {
              if (newPage > page) {
                if (imageQueriesForDetector?.next) {
                  const url = new URL(imageQueriesForDetector.next);
                  fetchNewImageQueries({
                    enteredDetectorId,
                    pageSize,
                    paginateFrom: url.searchParams.get("paginate_from"),
                  });
                }
              } else if (imageQueriesForDetector?.previous) {
                const url = new URL(imageQueriesForDetector.previous);
                fetchNewImageQueries({
                  enteredDetectorId,
                  pageSize,
                  paginateFrom: url.searchParams.get("paginate_from"),
                });
              }

              setPage(newPage);
            }}
            pageSize={openModal === "image-query" ? 1 : pageSize}
            setPageSize={(newPageSize) => {
              setPageSize(newPageSize);

              fetchNewImageQueries({
                enteredDetectorId,
                pageSize: newPageSize,
              });

              // reset page and pagination to avoid looking to a non-existing page after changing page size
              setPage(1);
            }}
            getData={async () => {
              if (openModal === "detector") {
                await fetchNewImageQueries({
                  enteredDetectorId,
                  pageSize,
                });
                setSelectedRowIndex(0);
              } else {
                // cleanup and closes modal when single image query is deleted
                setImageQueries([]);
                setOpenModal(null);
              }
            }}
            rows={
              // do not show rows while loading to avoid showing stale data from previous queries
              !isLoading ? imageQueries ?? [] : []
            }
            loading={isLoading}
            rowCount={openModal === "detector" ? imageQueriesForDetector?.count ?? 0 : 1}
            showDialog={openModal !== null}
            selectedRowIndex={selectedRowIndex}
            setSelectedRowIndex={setSelectedRowIndex}
          />
        </BoundingBoxesProvider>
      )}
    </>
  );
}
